import { Box, Divider, styled, Typography } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'

import { color } from 'config/color'
import { useAuth } from 'context'
import { useMobileBreakpoints } from 'utils'
import { FormLabel, MButton, ProgressLoaders, Tag } from 'components'
import { EditEmailDialog } from './EditEmailDialog'
import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { CLIENT } from 'config'

export const EmailVerification = (): JSX.Element => {
  const isMobile = useMobileBreakpoints()
  const { account, manualConfirmUser } = useAuth()
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { enqueueSnackbar } = useSnackbar()

  const [emails, setEmails] = useState<any>([])
  const isInvoiceCloudDisabled = process.env.REACT_APP_CLIENT_NAME !== CLIENT.WEB

  useEffect(() => {
    setEmails([
      {
        platform: 'AWS Cognito',
        email: account?.cognitoEmail,
        status: account?.status === 'CONFIRMED' ? 'Correct' : account?.status === 'UNCONFIRMED' ? 'UNCONFIRMED' : 'Mismatch'
      },
      {
        platform: 'Oracle Cloud Customer Service',
        email: account?.ccsEmail,
        status:
          account?.cognitoEmail?.toLowerCase() === account?.ccsEmail?.toLowerCase() ? 'Correct' : 'Mismatch'
      },
      ...(isInvoiceCloudDisabled
        ? [{
          platform: 'Invoice Cloud Authentication',
          email: account?.icEmail,
          status:
            account?.cognitoEmail === account?.icEmail ? 'Correct' : 'Mismatch'
        }]
        : [])
    ])
  }, [account?.ccsEmail, account?.cognitoEmail, account?.icEmail, account?.status, isInvoiceCloudDisabled])

  const handleConfirm = async (email: string): Promise<void> => {
    setIsLoading(true)
    try {
      const result: any = await manualConfirmUser({ email, accountId: account?.accountId })
      if (result.success === true) {
        enqueueSnackbar(
          'User has been confirmed. They can now login using the password they set when registering.',
          { variant: 'success', autoHideDuration: 3000 }
        )

        setTimeout(() => {
          window.location.reload()
        }, 4000)
      }
    } catch (error: any) {
      enqueueSnackbar(`Error confirming user: ${error.message}`, {
        variant: 'error'
      })
    }
    setIsLoading(false)
  }

  if (!account) {
    return (
      <Box border={`1px solid ${color.secondary.dark[300]}`} p={2}>
        <ProgressLoaders height="100%" />
      </Box>
    )
  }

  return (
    <Box border={`1px solid ${color.secondary.dark[300]}`}>
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        p={1}
        bgcolor={color.grey[50]}
        gap={isMobile ? 1 : 0}
      >
        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
          <EmailIcon
            sx={{
              color: color.grey[500],
              backgroundColor: color.grey[200],
              fontSize: 26,
              borderRadius: '50%',
              padding: 1
            }}
          />

          <Typography color={color.black} fontWeight={600} fontSize={16}>
            Email Verification
          </Typography>
        </Box>

        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>


          <MButton
            variant="outlined"
            type="button"
            rounded="true"
            texttransform="none"
            fullWidth={isMobile}
            onClick={() => {
              setIsDialogOpen(!isDialogOpen)
            }}
          >
            Update Email
          </MButton>

          {/* syncing AWS cognito email to Invoice Cloud */}
          {/* <MButton
            variant="outlined"
            type="button"
            rounded="true"
            disabled
            texttransform="none"
            fullWidth={isMobile}
            onClick={() => {
              setIsDialogOpen(!isDialogOpen)
            }}
          >
            Sync Email
          </MButton> */}
        </Box>
      </Box>

      <Divider sx={{ color: color.secondary.dark[300] }} />

      {isMobile
        ? (
          <Box>
            {emails.map((item: any, index: number) => {
              return (
                <EmailVerificationTableMobileView
                  platform={item.platform}
                  email={item.email}
                  status={item.status}
                  key={index}
                />
              )
            })}
          </Box>
        )
        : (
          <Box px={2} pb={2}>
            <Table>
              <TableHeader>
                <ItemWrapper width="40%">
                  <FormLabel textcolor={color.grey[600]}>Platform</FormLabel>
                </ItemWrapper>
                <ItemWrapper width="40%">
                  <FormLabel textcolor={color.grey[600]}>email</FormLabel>
                </ItemWrapper>
                <ItemWrapper width="20%">
                  <FormLabel textcolor={color.grey[600]}>Status</FormLabel>
                </ItemWrapper>
              </TableHeader>

              <TableContent>
                {emails.map((item: any, index: number) => {
                  return (
                    <TableItem key={index}>
                      <ItemWrapper width="40%">
                        <Typography color={color.grey[700]} fontSize={14}>
                          {item.platform}
                        </Typography>
                      </ItemWrapper>
                      <ItemWrapper width="40%">
                        <Typography color={color.grey[700]} fontSize={14}>
                          {item.email}
                        </Typography>
                      </ItemWrapper>
                      <ItemWrapper width="20%">
                        {item.status === 'UNCONFIRMED' && (
                          <MButton
                            variant="outlined"
                            type="button"
                            loading={isLoading}
                            rounded="true"
                            texttransform="none"
                            fullWidth={isMobile}
                            onClick={async () => {
                              await handleConfirm(item.email)
                            }}
                          >
                            Manual Confirm
                          </MButton>
                        )}
                        {item.status !== 'UNCONFIRMED' && (
                          <Tag
                            type={item.status === 'Correct' ? 'success' : 'error'}
                            message={item.status}
                            Icon={
                              item.status === 'Correct'
                                ? CheckCircleIcon
                                : ErrorIcon
                            }
                          />
                        )}
                      </ItemWrapper>
                    </TableItem>
                  )
                })}
              </TableContent>
            </Table>
          </Box>
        )}

      <EditEmailDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(!isDialogOpen)
        }}
        email={account?.cognitoEmail}
      />
    </Box>
  )
}

interface IEmailVerificationTableMobileView {
  platform: string
  email: string
  status: string
}

const EmailVerificationTableMobileView = (
  props: IEmailVerificationTableMobileView
): JSX.Element => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      p={2}
      gap={2}
      borderBottom={`solid 1px ${color.secondary.dark[100]}`}
    >
      <Box display="flex" flexDirection="column">
        <FormLabel textcolor={color.grey[600]}>Platform</FormLabel>
        <Typography color={color.grey[700]} fontSize={14}>
          {props?.platform}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <FormLabel textcolor={color.grey[600]}>Email</FormLabel>
        <Typography color={color.grey[700]} fontSize={14}>
          {props?.email}
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <FormLabel textcolor={color.grey[600]}>status</FormLabel>

        {props.status && (
          <Tag
            type={props.status === 'Correct' ? 'success' : 'error'}
            message={props.status}
            Icon={props.status === 'Correct' ? CheckCircleIcon : ErrorIcon}
          />
        )}
      </Box>
    </Box>
  )
}

const Table = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  background: color.white
})

const TableHeader = styled(Box)({
  display: 'flex',
  borderBottom: color.secondary.dark[300],
  fontSize: 14,
  fontWeight: 600
})

const TableContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 10
})

const TableItem = styled(Box)({
  display: 'flex',
  background: color.grey[100],
  borderBottom: color.secondary.dark[300],
  '&:hover': {
    backgroundColor: color.primary[50]
  }
})

const ItemWrapper = styled(Box)<{ width?: string }>(({ width }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  wordWrap: 'break-word',
  wordBreak: 'break-all',
  width,
  padding: 15,
  color: color.black,
  fontSize: 14,
  fontWeight: 400
}))
